import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supportAPI } from '../services/api';

type TalkToSalesForm = {
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  website: string;
  role: string;
  team_size: string;
  country: string;
  requirements: string;
  budget: string;
  timeline: string;
};

const defaultForm: TalkToSalesForm = {
  full_name: '',
  email: '',
  phone: '',
  company_name: '',
  website: '',
  role: '',
  team_size: '',
  country: '',
  requirements: '',
  budget: '',
  timeline: '',
};

const TalkToSales: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<TalkToSalesForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const onChange = (key: keyof TalkToSalesForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.requirements.trim()) {
      setError('Name, email, and requirements are required.');
      return;
    }

    try {
      setError(null);
      setSubmitting(true);
      await supportAPI.submitTalkToSales({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company_name: form.company_name.trim(),
        website: form.website.trim(),
        role: form.role.trim(),
        team_size: form.team_size.trim(),
        country: form.country.trim(),
        requirements: form.requirements.trim(),
        budget: form.budget.trim(),
        timeline: form.timeline.trim(),
      });
      setShowSuccessModal(true);
      setForm(defaultForm);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to submit your request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '920px' }}>
      <div className="card" style={{ marginTop: '24px' }}>
        <h1 className="h2">Talk to Sales</h1>
        <p className="muted">
          Tell us your custom requirements and our team will reach out with a tailored solution.
        </p>

        {error && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
            <div className="form-group">
              <label className="label">Full Name *</label>
              <input className="input" value={form.full_name} onChange={(e) => onChange('full_name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="label">Email *</label>
              <input type="email" className="input" value={form.email} onChange={(e) => onChange('email', e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="label">Phone</label>
              <input className="input" value={form.phone} onChange={(e) => onChange('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Company / Business</label>
              <input className="input" value={form.company_name} onChange={(e) => onChange('company_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Website</label>
              <input className="input" value={form.website} onChange={(e) => onChange('website', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Role / Designation</label>
              <input className="input" value={form.role} onChange={(e) => onChange('role', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Team Size</label>
              <input className="input" value={form.team_size} onChange={(e) => onChange('team_size', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Country</label>
              <input className="input" value={form.country} onChange={(e) => onChange('country', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Budget</label>
              <input className="input" value={form.budget} onChange={(e) => onChange('budget', e.target.value)} placeholder="e.g. INR 1L - 3L / month" />
            </div>
            <div className="form-group">
              <label className="label">Timeline</label>
              <input className="input" value={form.timeline} onChange={(e) => onChange('timeline', e.target.value)} placeholder="e.g. Need rollout in 2 weeks" />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '8px' }}>
            <label className="label">Custom Requirements *</label>
            <textarea
              className="input"
              rows={6}
              value={form.requirements}
              onChange={(e) => onChange('requirements', e.target.value)}
              placeholder="Describe your business requirement, use-case, user volume, integrations, and expected outcomes."
              required
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
            <button type="button" className="btn ghost" onClick={() => navigate('/pricing')} disabled={submitting}>
              Back to Pricing
            </button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>

      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Request Submitted</h3>
              <button className="modal-close" onClick={() => setShowSuccessModal(false)} aria-label="Close">&times;</button>
            </div>
            <div className="modal-body">
              <p className="muted">
                Thank you. Our team will reach out to you shortly.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/pricing');
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalkToSales;
